import { useCallback, useSyncExternalStore } from 'react';

/**
 * A media query as a React value.
 *
 * `useSyncExternalStore` rather than `useEffect` + `setState`: the latter
 * renders once with the wrong answer and then corrects itself, which for a
 * layout switch means the virtual table mounts and measures before the card
 * list replaces it.
 *
 * `subscribe` is memoised on the query because `useSyncExternalStore` tears the
 * subscription down and rebuilds it whenever that function's identity changes —
 * an inline closure would re-register a listener on every render.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void): (() => void) => {
      const list = window.matchMedia(query);
      list.addEventListener('change', onChange);
      return () => {
        list.removeEventListener('change', onChange);
      };
    },
    [query],
  );

  const getSnapshot = useCallback((): boolean => window.matchMedia(query).matches, [query]);

  // No server snapshot: admin is a client-only SPA with no hydration pass, and
  // an unused third argument would be an untestable branch pretending to
  // support a rendering mode that does not exist here.
  return useSyncExternalStore(subscribe, getSnapshot);
}

/**
 * The breakpoint below which the post table stops being a table (AM-15.1).
 *
 * `VirtualAdminTable` fixes `gridTemplateColumns` and absolutely positions
 * every row, so it cannot reflow into stacked cards at any width. The list page
 * swaps components here instead.
 */
export const STACKED_CARDS_QUERY = '(max-width: 719px)';
